import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import isEmpty from 'lodash-es/isEmpty';

import { OptionSelected } from '@booking/entities/booking-arrangement';
import { TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { FormInputType, FormDocumentInputTemplate } from '@forms/form-text-input/entities/input-type';
import { LoyaltyResponse, LoyaltyPayload } from '@pax/entities/traveler-pax';
import { FormValidationService } from '@reservation/services/form-validation.service';
import { FrequentFlyerNamePipe } from '@app/pipes/frequent-flyer-name.pipe';

@Component({
  selector: 'app-loyalty-form',
  templateUrl: './loyalty-form.component.html',
  styleUrls: ['./loyalty-form.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LoyaltyFormComponent implements OnInit {

  @Input() programs: FrequentFlyerResponse[] = [];
  @Input() defaultAirline: string;
  @Input() selectedLoyalty: LoyaltyResponse;
  @Input() form: UntypedFormGroup;
  @Input() isMobile: boolean;

  @Output() create: EventEmitter<LoyaltyPayload> = new EventEmitter();
  @Output() update: EventEmitter<LoyaltyResponse> = new EventEmitter();

  public flyerSelector: string = TRAVELER_FORM_INPUTS.FREQUENT_FLYER;
  public documentInput: FormInputType = FormInputType.DOCUMENT;
  public alphanumericType: FormDocumentInputTemplate = FormDocumentInputTemplate.ALPHANUMERIC;
  public loyaltyForm: UntypedFormGroup;
  public isSubmitted: boolean = false;

  constructor(
    private readonly frequentFlyerNamePipe: FrequentFlyerNamePipe,
    private readonly formValidationService: FormValidationService,
  ) { }

  ngOnInit(): void {
    this.createLoyaltyForm();
  }

  get hasSelectedLoyalty(): boolean {
    return !isEmpty(this.selectedLoyalty);
  }

  get defaultAirlineProgram$(): Observable<string> {
    return this.frequentFlyerNamePipe.transform(this.defaultLoyalty);
  }

  get defaultLoyalty(): string {
    return this.hasSelectedLoyalty ? this.selectedLoyalty.airline : this.defaultAirline;
  }

  get defaultLoyaltyNumber(): string {
    return this.hasSelectedLoyalty ? this.selectedLoyalty.number : '';
  }

  public onSelectedFrequentFlyerAirline(event: OptionSelected): void {
    const program = this.programs.find(item => item.program_name.includes(event.value as string));
    if (program) this.loyaltyForm.patchValue({ [TRAVELER_FORM_INPUTS.FREQUENT_FLYER_AIRLINE]: program.airline_code });
  }

  public createLoyaltyForm(): void {
    this.loyaltyForm = new UntypedFormGroup({
      [TRAVELER_FORM_INPUTS.FREQUENT_FLYER_AIRLINE]: new UntypedFormControl(
        this.defaultLoyalty, Validators.required),
      [TRAVELER_FORM_INPUTS.FREQUENT_FLYER]: new UntypedFormControl(
        this.defaultLoyaltyNumber, Validators.required),
    });
  }

  public save(): void {
    this.isSubmitted = true;
    if (!this.formValidationService.formInvalidControls(this.loyaltyForm)) {
      const values = this.loyaltyForm.value;
      this.form?.patchValue({
        [TRAVELER_FORM_INPUTS.FREQUENT_FLYER_AIRLINE]: values[TRAVELER_FORM_INPUTS.FREQUENT_FLYER_AIRLINE],
        [TRAVELER_FORM_INPUTS.FREQUENT_FLYER]: values[TRAVELER_FORM_INPUTS.FREQUENT_FLYER]
      });
      if (!this.hasSelectedLoyalty) {
        this.create.emit({
          airline: values[TRAVELER_FORM_INPUTS.FREQUENT_FLYER_AIRLINE],
          number: values[TRAVELER_FORM_INPUTS.FREQUENT_FLYER]
        });
      } else {
        this.update.emit({
          ...this.selectedLoyalty,
          airline: values[TRAVELER_FORM_INPUTS.FREQUENT_FLYER_AIRLINE],
          number: values[TRAVELER_FORM_INPUTS.FREQUENT_FLYER]
        });
      }
    }
  }
}
